import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PS } from '../theme';
import { Icon } from '../lib/icons';
import MemberLayout from '../components/MemberLayout';
import { useAuth } from '../auth/AuthContext';
import api from '../lib/api';
import { notifySuccess, notifyError, confirmToast } from '../lib/toast';

function Toggle({ on, busy, onClick }) {
  return (
    <button data-testid="admin-sim-toggle" aria-pressed={on} disabled={busy} onClick={onClick} className="ps-press"
      style={{ width: 56, height: 32, borderRadius: 999, border: 'none', position: 'relative', cursor: busy ? 'wait' : 'pointer',
        background: on ? PS.forest : PS.line, transition: 'background-color 0.2s ease', flexShrink: 0, opacity: busy ? 0.6 : 1 }}>
      <span style={{ position: 'absolute', top: 3, left: on ? 27 : 3, width: 26, height: 26, borderRadius: '50%',
        background: '#fff', boxShadow: '0 1px 3px rgba(0,0,0,0.25)', transition: 'left 0.2s ease' }} />
    </button>
  );
}

export default function Admin() {
  const nav = useNavigate();
  const { loading: authLoading } = useAuth();
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [showSim, setShowSim] = useState(false);
  const [simCount, setSimCount] = useState(0);
  const [saving, setSaving] = useState(false);
  const [devEnabled, setDevEnabled] = useState(false);
  const [devCount, setDevCount] = useState(100);
  const [purging, setPurging] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    let alive = true;
    (async () => {
      try {
        const me = await api.get('/admin/me');
        if (!alive) return;
        if (!me.data.is_admin) { setIsAdmin(false); setLoading(false); return; }
        setIsAdmin(true);
        const s = await api.get('/admin/settings');
        if (!alive) return;
        setShowSim(!!s.data.show_sim_data);
        setSimCount(s.data.sim_circles || 0);
        try {
          const d = await api.get('/auth/dev-login/status');
          if (alive) { setDevEnabled(!!d.data.enabled); setDevCount(d.data.count || 100); }
        } catch { /* noop */ }
      } catch {
        if (alive) setIsAdmin(false);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [authLoading]);

  const toggle = async () => {
    const next = !showSim;
    setSaving(true);
    try {
      const res = await api.patch('/admin/settings', { show_sim_data: next });
      setShowSim(!!res.data.show_sim_data);
      setSimCount(res.data.sim_circles ?? simCount);
      notifySuccess(next
        ? 'เปิดการแสดงข้อมูลจาก test agent (sim) บนหน้าสาธารณะแล้ว'
        : 'ปิดการแสดงข้อมูลจาก test agent (sim) แล้ว — หน้าสาธารณะแสดงเฉพาะวงจริง');
    } catch {
      notifyError('บันทึกไม่สำเร็จ ลองอีกครั้ง');
    } finally {
      setSaving(false);
    }
  };

  const purgeSim = () => {
    confirmToast('ล้างข้อมูล sim ทั้งหมด?', {
      confirmLabel: 'ล้างเลย',
      description: 'ลบวง/หลักฐาน/แชทของบัญชีทดสอบทั้งหมด — ไม่กระทบวงจริงและวง demo',
      onConfirm: async () => {
        setPurging(true);
        try {
          const res = await api.post('/sim/purge');
          notifySuccess(`ล้างข้อมูล sim แล้ว · ${res.data.purged_circles || 0} วง`);
          try {
            const s = await api.get('/admin/settings');
            setSimCount(s.data.sim_circles || 0);
          } catch { /* noop */ }
        } catch {
          notifyError('ล้างไม่สำเร็จ — ต้องเปิด DEV_LOGIN_ENABLED ก่อน');
        } finally {
          setPurging(false);
        }
      },
    });
  };

  return (
    <MemberLayout>
      <div style={{ padding: '14px 20px 12px', flexShrink: 0 }}>
        <div style={{ fontSize: 22, fontWeight: 600, letterSpacing: -0.3 }}>ผู้ดูแลระบบ</div>
        <div style={{ fontSize: 12, color: PS.textMuted, marginTop: 2 }}>ตั้งค่าการแสดงผลข้อมูลบนแพลตฟอร์ม</div>
      </div>

      <div style={{ flex: 1, overflow: 'auto', paddingBottom: 24 }}>
        {loading && (
          <div data-testid="admin-loading" style={{ padding: 30, textAlign: 'center', color: PS.textMuted, fontSize: 13 }}>กำลังโหลด…</div>
        )}

        {!loading && !isAdmin && (
          <div data-testid="admin-denied" style={{ margin: '12px 16px', padding: 24, background: PS.surface, border: `1px solid ${PS.line}`, borderRadius: 18, textAlign: 'center' }}>
            <div style={{ width: 44, height: 44, borderRadius: 14, background: 'oklch(0.62 0.12 28 / 0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px' }}>
              <Icon.lock size={20} stroke={PS.danger} />
            </div>
            <div style={{ fontSize: 15, fontWeight: 600 }}>ไม่มีสิทธิ์เข้าถึง</div>
            <div style={{ fontSize: 12.5, color: PS.textMuted, marginTop: 6, lineHeight: 1.6 }}>หน้านี้สำหรับผู้ดูแลระบบเท่านั้น</div>
            <button data-testid="admin-back-btn" onClick={() => nav('/app')} className="ps-press"
              style={{ marginTop: 16, background: PS.ink, color: PS.page, border: 'none', padding: '10px 20px', borderRadius: 999, fontSize: 13, fontWeight: 600, fontFamily: PS.fontUI }}>
              กลับหน้าหลัก
            </button>
          </div>
        )}

        {!loading && isAdmin && (
          <div data-testid="admin-page" style={{ margin: '0 16px' }}>
            <div style={{ background: PS.surface, border: `1px solid ${PS.line}`, borderRadius: 18, padding: 18 }}>
              <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start', justifyContent: 'space-between' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 14.5, fontWeight: 600, color: PS.text }}>แสดงข้อมูลจาก test agent (sim)</div>
                  <div style={{ fontSize: 12, color: PS.textMuted, marginTop: 6, lineHeight: 1.65 }}>
                    เมื่อ<b>เปิด</b> วงที่สร้างโดยบัญชีทดสอบ (sim) จะปรากฏใน “สำรวจวงแชร์”, หมุดบนแผนที่ และตัวนับสถิติสาธารณะ
                    เพื่อให้คุณดูผลการจำลองได้ · เมื่อ<b>ปิด</b> (ค่าเริ่มต้น) หน้าสาธารณะจะแสดงเฉพาะ<b>วงจริง</b>เท่านั้น
                  </div>
                </div>
                <Toggle on={showSim} busy={saving} onClick={toggle} />
              </div>
              <div data-testid="admin-sim-status" style={{ marginTop: 14, paddingTop: 14, borderTop: `1px solid ${PS.lineSoft}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, fontWeight: 600, color: showSim ? PS.forest : PS.textMuted }}>
                  <span style={{ width: 9, height: 9, borderRadius: '50%', background: showSim ? PS.forest : PS.textDim, display: 'inline-block' }} />
                  {showSim ? 'กำลังแสดงข้อมูล sim บนหน้าสาธารณะ' : 'ซ่อนข้อมูล sim (แสดงเฉพาะวงจริง)'}
                </div>
                <div data-testid="admin-sim-count" style={{ fontSize: 11.5, color: PS.textMuted }}>
                  วง sim ในระบบ: <b className="num" style={{ color: PS.text }}>{simCount}</b> วง
                </div>
              </div>
            </div>

            <div style={{ marginTop: 14, padding: '12px 14px', background: PS.panel, borderRadius: 14, fontSize: 11.5, color: PS.textMuted, lineHeight: 1.7, display: 'flex', gap: 9 }}>
              <Icon.info size={15} stroke={PS.textMuted} style={{ flexShrink: 0, marginTop: 1 }} />
              <span>ค่านี้มีผลทันทีกับทุกผู้ใช้ · ไม่กระทบวง demo (ที่มีป้าย “ตัวอย่าง”) ซึ่งแสดงเสมอ และไม่กระทบวงจริงของผู้ใช้ · <b>วิธีทดสอบในของจริง:</b> เปิดสวิตช์ → เปิดหน้า “สำรวจวงแชร์” เพื่อดูว่าวง sim ปรากฏ → ทดสอบเสร็จแล้ว<b>ปิดกลับ</b></span>
            </div>
            <button data-testid="admin-open-discover" onClick={() => nav('/app/discover')} className="ps-press"
              style={{ marginTop: 12, background: 'transparent', color: PS.text, border: `1px solid ${PS.line}`, padding: '10px 18px', borderRadius: 999, fontSize: 13, fontWeight: 600, fontFamily: PS.fontUI, display: 'inline-flex', alignItems: 'center', gap: 8 }}>
              <Icon.search size={15} stroke={PS.text} /> เปิดหน้าสำรวจวงแชร์เพื่อตรวจการมองเห็น
            </button>

            {/* Playtest / test-account system */}
            <div data-testid="admin-playtest" style={{ marginTop: 22, background: PS.surface, border: `1px solid ${PS.line}`, borderRadius: 18, padding: 18 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'space-between' }}>
                <div style={{ fontSize: 14.5, fontWeight: 600 }}>ระบบทดสอบ (Playtest)</div>
                <div data-testid="admin-devlogin-status" style={{ fontSize: 11, fontWeight: 700, padding: '4px 10px', borderRadius: 999, background: devEnabled ? 'oklch(0.78 0.06 155 / 0.35)' : PS.panel, color: devEnabled ? PS.forest : PS.textMuted }}>
                  {devEnabled ? 'เปิดใช้งาน' : 'ปิดอยู่'}
                </div>
              </div>
              <div style={{ fontSize: 12, color: PS.textMuted, marginTop: 6, lineHeight: 1.65 }}>
                เข้าสู่ระบบด้วยบัญชีทดสอบ <b>{devCount}</b> บัญชี (ไม่ต้องผ่าน Google) เพื่อจำลองการเล่นแชร์ · {devEnabled
                  ? 'พร้อมใช้งาน'
                  : 'ต้องตั้ง env DEV_LOGIN_ENABLED=true แล้ว redeploy ก่อนจึงจะใช้ได้'}
              </div>
              <div style={{ display: 'flex', gap: 8, marginTop: 14, flexWrap: 'wrap' }}>
                <button data-testid="admin-devlogin-btn" disabled={!devEnabled} onClick={() => nav('/app/dev-login')} className="ps-press"
                  style={{ background: devEnabled ? PS.ink : PS.panel, color: devEnabled ? PS.page : PS.textDim, border: 'none', padding: '11px 18px', borderRadius: 999, fontSize: 13, fontWeight: 600, fontFamily: PS.fontUI, display: 'inline-flex', alignItems: 'center', gap: 8, cursor: devEnabled ? 'pointer' : 'not-allowed' }}>
                  <Icon.user size={15} stroke={devEnabled ? PS.page : PS.textDim} /> เข้าสู่ระบบบัญชีทดสอบ
                </button>
                <button data-testid="admin-purge-btn" disabled={purging} onClick={purgeSim} className="ps-press"
                  style={{ background: 'transparent', color: PS.danger, border: `1px solid ${PS.danger}`, padding: '11px 18px', borderRadius: 999, fontSize: 13, fontWeight: 600, fontFamily: PS.fontUI, display: 'inline-flex', alignItems: 'center', gap: 8, opacity: purging ? 0.6 : 1 }}>
                  <Icon.x size={15} stroke={PS.danger} /> ล้างข้อมูล sim ({simCount})
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </MemberLayout>
  );
}
